import { useState, useEffect, useCallback, useRef } from 'react';
import { normalizeAdminListResponse } from '../utils/adminListResponse';

const DEFAULT_PAGE_SIZE = 30;

/**
 * Infinite scroll helper for admin list APIs returning { data, pagination }.
 * Refetches when getParams() JSON or resetKey changes (search/filters).
 * In-flight responses from a previous filter set are ignored so stale results
 * cannot overwrite a newer search/filter request.
 */
export function useAdminInfiniteList({ load, getParams, pageSize = DEFAULT_PAGE_SIZE, resetKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    hasMore: false,
    total: 0,
    page: 1,
    limit: pageSize
  });

  const pageRef = useRef(1);
  const requestIdRef = useRef(0);
  const getParamsRef = useRef(getParams);
  getParamsRef.current = getParams;

  const runFetch = useCallback(
    async (page, append, requestId) => {
      const id = requestId ?? requestIdRef.current;
      const base = typeof getParamsRef.current === 'function' ? getParamsRef.current() : {};
      const res = await load({ ...base, page, limit: pageSize });
      if (id !== requestIdRef.current) return false;
      const { data, pagination: pag } = normalizeAdminListResponse(res);
      pageRef.current = page;
      setPagination({
        hasMore: Boolean(pag.hasMore),
        total: Number(pag.total) || 0,
        page: Number(pag.page) || page,
        limit: Number(pag.limit) || pageSize,
        totalPages: Number(pag.totalPages) || 1
      });
      if (append) {
        setItems((prev) => [...prev, ...data]);
      } else {
        setItems(data);
      }
      return true;
    },
    [load, pageSize]
  );

  const paramsKey = JSON.stringify({
    params: typeof getParams === 'function' ? getParams() : {},
    resetKey: resetKey ?? null
  });

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    pageRef.current = 1;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    runFetch(1, false, requestId)
      .catch((e) => {
        if (requestId === requestIdRef.current) setError(e);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [paramsKey, runFetch]);

  const loadMore = useCallback(async () => {
    if (!pagination.hasMore || loadingMore || loading) return;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    setError(null);
    const nextPage = pageRef.current + 1;
    try {
      await runFetch(nextPage, true, requestId);
    } catch (e) {
      if (requestId === requestIdRef.current) setError(e);
    } finally {
      if (requestId === requestIdRef.current) setLoadingMore(false);
    }
  }, [pagination.hasMore, loadingMore, loading, runFetch]);

  const reload = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    pageRef.current = 1;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    try {
      await runFetch(1, false, requestId);
    } catch (e) {
      if (requestId === requestIdRef.current) setError(e);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [runFetch]);

  return { items, loading, loadingMore, error, pagination, loadMore, reload };
}

/**
 * Attach near-bottom scroll loading to a scrollable element ref.
 */
export function useNearBottomScroll(loadMore, { thresholdPx = 120, enabled = true } = {}) {
  const ref = useRef(null);
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return undefined;

    const onScroll = () => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < thresholdPx) {
        loadMoreRef.current();
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [thresholdPx, enabled]);

  return ref;
}
